import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
  Paper,
  Badge,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  FilterList,
} from "@mui/icons-material";
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  eachDayOfInterval,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { getSchedulesByPeriod, deleteSchedule } from "../services";
import { ISchedule, ScheduleStatus } from "../types";
import ModalAgendamento from "../components/ModalAgendamento";
import AgendamentoCard from "../components/AgendamentoCard";
import { useNotifications } from "../contexts/NotificationContext";

type ViewMode = "week" | "month";

const Agenda: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { refreshNotifications } = useNotifications();
  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ISchedule | null>(
    null
  );
  const [viewMode, setViewMode] = useState<ViewMode>("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterStatus, setFilterStatus] = useState<string>("all");

  // Usar useRef para guardar posição do scroll
  const scrollPositionRef = useRef<number>(0);

  // Salvar posição do scroll antes de recarregar
  const saveScrollPosition = useCallback(() => {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    scrollPositionRef.current = scrollY;
  }, []);

  // Restaurar posição do scroll após recarregar
  const restoreScrollPosition = useCallback(() => {
    if (scrollPositionRef.current > 0) {
      setTimeout(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: "auto",
        });
      }, 50);
    }
  }, []);

  // Calcular período visível baseado no modo de visualização
  const getPeriod = useCallback(() => {
    if (viewMode === "week") {
      return {
        start: startOfWeek(currentDate, { locale: ptBR }),
        end: endOfWeek(currentDate, { locale: ptBR }),
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      };
    }
  }, [currentDate, viewMode]);

  // Carregar agendamentos
  const loadSchedules = useCallback(
    async (preserveScroll = false) => {
      setLoading(true);
      setError(null);
      try {
        const period = getPeriod();
        const data = await getSchedulesByPeriod(
          period.start.toISOString(),
          period.end.toISOString()
        );
        setSchedules(data || []);

        // Restaurar scroll após o DOM ser atualizado
        if (preserveScroll) {
          restoreScrollPosition();
        }
      } catch (err) {
        setError("Erro ao carregar agendamentos. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [getPeriod, restoreScrollPosition]
  );

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  // Navegação de período
  const handlePreviousPeriod = () => {
    if (viewMode === "week") {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(subMonths(currentDate, 1));
    }
  };

  const handleNextPeriod = () => {
    if (viewMode === "week") {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Filtrar agendamentos
  const filteredSchedules = schedules.filter((schedule) => {
    if (filterStatus === "all") return true;
    return schedule.status === filterStatus;
  });

  // Agrupar agendamentos por dia
  const groupSchedulesByDay = () => {
    const period = getPeriod();
    const days = eachDayOfInterval({ start: period.start, end: period.end });

    const grouped: { [key: string]: ISchedule[] } = {};

    days.forEach((day) => {
      const dayKey = format(day, "yyyy-MM-dd");
      grouped[dayKey] = [];
    });

    filteredSchedules.forEach((schedule) => {
      const dayKey = format(new Date(schedule.scheduledAt), "yyyy-MM-dd");
      if (grouped[dayKey]) {
        grouped[dayKey].push(schedule);
      }
    });

    return grouped;
  };

  // Handlers de modal
  const handleOpenModal = (schedule?: ISchedule) => {
    setSelectedSchedule(schedule || null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedSchedule(null);
    setModalOpen(false);
  };

  const handleSaveSchedule = () => {
    saveScrollPosition(); // Salvar scroll ANTES de recarregar
    loadSchedules(true); // Preservar scroll ao salvar
    handleCloseModal();
  };

  // Handler para mudança de status (preservar scroll)
  const handleStatusChange = () => {
    saveScrollPosition(); // Salvar scroll ANTES de recarregar
    loadSchedules(true); // Preservar scroll ao mudar status
  };

  // Handler de delete - executa diretamente (confirmação já foi feita no Card)
  const handleDeleteClick = async (scheduleId: string) => {
    saveScrollPosition(); // Salvar scroll antes de deletar

    try {
      await deleteSchedule(scheduleId);
      setSchedules(schedules.filter((s) => s.id !== scheduleId));

      // Atualizar lista de notificações para refletir exclusão em cascata
      await refreshNotifications();

      // Restaurar scroll após o DOM ser atualizado
      setTimeout(restoreScrollPosition, 100);
    } catch (err) {
      setError("Erro ao deletar agendamento. Tente novamente.");
    }
  };

  // Obter título do período
  const getPeriodTitle = () => {
    if (viewMode === "week") {
      const period = getPeriod();
      return `${format(period.start, "d MMM", { locale: ptBR })} - ${format(
        period.end,
        "d MMM yyyy",
        { locale: ptBR }
      )}`;
    } else {
      return format(currentDate, "MMMM yyyy", { locale: ptBR });
    }
  };

  const groupedSchedules = groupSchedulesByDay();

  // Contar agendamentos por status
  const statusCount = schedules.reduce(
    (acc, schedule) => {
      acc[schedule.status] = (acc[schedule.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Estado de carregamento inicial
  if (loading && schedules.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        pt: 0,
        mt: { xs: -2, sm: -2, md: -2.5 },
        width: "100%",
        maxWidth: "1920px",
        minWidth: 0,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header com Título e Botão Novo */}
      <Box
        sx={{
          mb: { xs: 1.5, sm: 1.5, md: 2 },
          display: "flex",
          flexDirection: { xs: "row", sm: "row", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} color="text.primary">
              Agenda
            </Typography>
            <Typography
              variant={isMobile ? "body2" : "body1"}
              color="text.secondary"
            >
              Gerencie seus agendamentos
            </Typography>
          </Box>
          <CalendarToday color="primary" sx={{ mt: 1.25, ml: -12.5 }} />
        </Box>
        <Button
          variant="contained"
          size={isMobile ? "small" : "medium"}
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          {isMobile ? "Novo" : "Novo Agendamento"}
        </Button>
      </Box>

      {/* Navegação e Filtros - Fora da box de scroll */}
      <Box
        sx={{
          mb: { xs: 2, sm: 2, md: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row", md: "row" },
          gap: { xs: 1.5, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap", // Adicionado para melhor responsividade
        }}
      >
        {/* Navegação de Período */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size={isMobile ? "small" : "medium"}
              onClick={handlePreviousPeriod}
              color="primary"
            >
              <ChevronLeft />
            </IconButton>
            <Button
              size={isMobile ? "small" : "medium"}
              onClick={handleToday}
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Hoje
            </Button>
            <IconButton
              size={isMobile ? "small" : "medium"}
              onClick={handleNextPeriod}
              color="primary"
            >
              <ChevronRight />
            </IconButton>
          </Box>
          <Chip
            label={getPeriodTitle()}
            color="primary"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          />
        </Box>

        {/* Controles: View Mode e Filtro */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 1, sm: 2 },
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size={isMobile ? "small" : "medium"}
            color="primary"
          >
            <ToggleButton value="week">Semana</ToggleButton>
            <ToggleButton value="month">Mês</ToggleButton>
          </ToggleButtonGroup>

          <FormControl
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: { xs: 150, sm: 180, md: 200 }, flexGrow: 1 }}
          >
            <InputLabel>Filtrar Status</InputLabel>
            <Select
              value={filterStatus}
              label="Filtrar Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
            >
              <MenuItem value="all">Todos ({schedules.length})</MenuItem>
              <MenuItem value={ScheduleStatus.PENDENTE}>
                Pendente ({statusCount[ScheduleStatus.PENDENTE] || 0})
              </MenuItem>
              <MenuItem value={ScheduleStatus.CONFIRMADO}>
                Confirmado ({statusCount[ScheduleStatus.CONFIRMADO] || 0})
              </MenuItem>
              <MenuItem value={ScheduleStatus.EM_ANDAMENTO}>
                Em Andamento ({statusCount[ScheduleStatus.EM_ANDAMENTO] || 0})
              </MenuItem>
              <MenuItem value={ScheduleStatus.CONCLUIDO}>
                Concluído ({statusCount[ScheduleStatus.CONCLUIDO] || 0})
              </MenuItem>
              <MenuItem value={ScheduleStatus.CANCELADO}>
                Cancelado ({statusCount[ScheduleStatus.CANCELADO] || 0})
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Mensagem de erro */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Container com scroll para agendamentos */}
      <Box
        sx={{
          width: "100%",
          minHeight: {
            xs: "calc(100vh - 300px)",
            sm: "calc(100vh - 420px)",
            md: "calc(100vh - 300px)",
            lg: "calc(100vh - 300px)",
            xl: "calc(100vh - 310px)",
          },
          height: {
            xs: "calc(100vh - 300px)",
            sm: "calc(100vh - 420px)",
            md: "calc(100vh - 300px)",
            lg: "calc(100vh - 300px)",
            xl: "calc(100vh - 310px)",
          },
          overflowY: {
            xs: "auto",
            sm: "auto",
            md: "auto",
            lg: "auto",
            xl: "auto",
          },
          mb: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {/* Lista de Agendamentos */}
        {Object.keys(groupedSchedules).length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              textAlign: "center",
              bgcolor: "grey.50",
              borderRadius: 2,
            }}
          >
            <CalendarToday
              sx={{
                fontSize: { xs: 48, sm: 56, md: 64 },
                color: "grey.400",
                mb: 2,
              }}
            />
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              color="text.secondary"
              gutterBottom
            >
              Nenhum agendamento no período 📅
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clique em &quot;Novo Agendamento&quot; para criar um
            </Typography>
          </Paper>
        ) : (
          Object.entries(groupedSchedules).map(([dayKey, daySchedules]) => {
            const date = new Date(dayKey + "T00:00:00");
            const todayCheck = format(new Date(), "yyyy-MM-dd") === dayKey;

            return (
              <Box key={dayKey} sx={{ mb: 2, width: "100%" }}>
                <Paper
                  elevation={todayCheck ? 3 : 1}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    width: "100%",
                    bgcolor: todayCheck ? "primary.50" : "background.paper",
                    borderLeft: todayCheck ? 4 : 0,
                    borderColor: "primary.main",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      elevation: 3,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: daySchedules.length > 0 ? 1 : 0,
                    }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle2" : "subtitle1"}
                      fontWeight="bold"
                      sx={{
                        color: todayCheck ? "primary.main" : "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {format(date, "EEEE, d 'de' MMMM", { locale: ptBR })}
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                      {todayCheck && (
                        <Chip
                          label="Hoje"
                          color="primary"
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                      <Badge
                        badgeContent={daySchedules.length}
                        color="primary"
                        sx={{ ml: 1 }}
                      >
                        <CalendarToday fontSize="small" />
                      </Badge>
                    </Box>
                  </Box>

                  {daySchedules.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ pl: 1 }}
                    >
                      Sem agendamentos
                    </Typography>
                  ) : (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {daySchedules
                          .sort(
                            (a, b) =>
                              new Date(a.scheduledAt).getTime() -
                              new Date(b.scheduledAt).getTime()
                          )
                          .map((schedule) => (
                            <AgendamentoCard
                              key={schedule.id}
                              schedule={schedule}
                              onEdit={() => handleOpenModal(schedule)}
                              onDelete={() => handleDeleteClick(schedule.id)}
                              onStatusChange={handleStatusChange}
                            />
                          ))}
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            );
          })
        )}
      </Box>

      {/* Modal de agendamento */}
      <ModalAgendamento
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveSchedule}
        schedule={selectedSchedule}
      />
    </Box>
  );
};

export default Agenda;
